import CustomModal from "components/Common/CustomModal";
import React, { useState } from "react";
import { inputStyle } from "./CreateOrder";
import api from "api";
import { toast } from "react-toastify";

const labelStyle = {
    marginTop: "10px",
    fontWeight: 600,
    fontSize: "16px",
    fontFamily: "Outfit",
    color: "#1C1C1C",
};

const hintStyle = {
    color: "#828282",
    fontSize: "13px",
    fontWeight: 400,
    fontFamily: "Outfit",
    display: "block",
    marginTop: "4px",
};

const sampleCode = (prefix, length) => {
    const chars = "G49DM3FD2W";
    return (prefix || "") + chars.substring(0, length || 6);
};

const OrderSettings = ({ handleModal, selectedOrder, getOrders }) => {
    const [loading, setLoading] = useState(false);
    // Lock code type if any batch is approved/processing
    const hasApprovedBatches = selectedOrder?.batchIds?.some(
        (b) => b.status === "approved" || b.status === "processing"
    );
    const [data, setData] = useState({
        code_type: selectedOrder?.code_type ?? "regular",
        prefix: selectedOrder?.prefix ?? "",
        code_length: selectedOrder?.code_length ?? "6",
        externalBaseUrl: selectedOrder?.externalBaseUrl ?? "",
    });

    const handleSave = () => {
        if (data.code_type === "regular" && data.prefix && !/^[A-Z0-9]{0,2}$/.test(data.prefix)) {
            return toast.error("Prefix must be 1-2 uppercase alphanumeric characters");
        }
        setLoading(true);
        const obj = {
            code_type: data.code_type,
            prefix: data.code_type === "regular" ? data.prefix : "",
            code_length: data.code_type === "regular" ? data.code_length : "16",
            externalBaseUrl: (data.code_type === "nfc" || data.code_type === "codeless") ? data.externalBaseUrl : "",
        };
        api("patch", `/orders/${selectedOrder._id}`, obj)
            .then((res) => {
                setLoading(false);
                toast.success(res.message || "Order settings updated");
                handleModal();
                getOrders();
            })
            .catch((err) => {
                setLoading(false);
                toast.error(err?.response?.data?.message || "Failed to update order settings");
            });
    };

    const handlePrefixChange = (e) => {
        const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
        if (value.length <= 2) {
            setData((prev) => ({ ...prev, prefix: value }));
        }
    };

    return (
        <CustomModal
            handleSave={handleSave}
            heading="Order Settings"
            buttonName="Save"
            handleModal={handleModal}
            loading={loading}
        >
            {/* ── Code Type ── */}
            <label style={labelStyle}>
                Code Type
                <span style={hintStyle}>
                    {hasApprovedBatches
                        ? "Locked — cannot change code type after batches have been approved"
                        : "Determines how codes are generated for all batches in this order"}
                </span>
            </label>
            <div className="d-flex" style={{ gap: "8px", marginTop: "6px" }}>
                {[
                    { value: "regular", label: "Regular", tip: "QR codes with custom prefix and length" },
                    { value: "nfc", label: "NFC", tip: "16-char codes for NFC chip writing" },
                    { value: "codeless", label: "Codeless", tip: "16-char codes linked to external URL" },
                ].map((opt) => (
                    <button
                        key={opt.value}
                        type="button"
                        title={hasApprovedBatches ? "Locked — approved batches exist" : opt.tip}
                        disabled={hasApprovedBatches}
                        onClick={() => !hasApprovedBatches && setData((prev) => ({ ...prev, code_type: opt.value }))}
                        className={data.code_type === opt.value ? "vs-btn-save" : "vs-btn-cancel"}
                        style={{
                            width: "auto", padding: "8px 18px", fontSize: "13px",
                            opacity: hasApprovedBatches ? 0.5 : 1,
                            cursor: hasApprovedBatches ? "not-allowed" : "pointer",
                        }}
                    >
                        {opt.label}
                    </button>
                ))}
            </div>

            {/* ── Regular code settings (prefix + length) ── */}
            {data.code_type === "regular" && (
                <div style={{ marginTop: "12px", padding: "12px", backgroundColor: "#f9f9fb", borderRadius: "8px" }}>
                    <div className="d-flex" style={{ gap: "12px" }}>
                        <div style={{ flex: 1 }}>
                            <label style={{ ...labelStyle, marginTop: 0 }}>
                                Prefix
                                <span style={hintStyle}>1-2 uppercase letters/numbers prepended to each code</span>
                            </label>
                            <input
                                onChange={handlePrefixChange}
                                value={data.prefix}
                                placeholder="e.g. VS"
                                style={{ ...inputStyle, opacity: hasApprovedBatches ? 0.5 : 1 }}
                                type="text"
                                maxLength={2}
                                disabled={hasApprovedBatches}
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <label style={{ ...labelStyle, marginTop: 0 }}>
                                Code Length
                                <span style={hintStyle}>Character count per code (excluding prefix)</span>
                            </label>
                            <select
                                value={data.code_length}
                                onChange={(e) => setData((prev) => ({ ...prev, code_length: e.target.value }))}
                                style={{ ...inputStyle, appearance: "auto", opacity: hasApprovedBatches ? 0.5 : 1 }}
                                disabled={hasApprovedBatches}
                            >
                                <option value="6">6</option>
                                <option value="7">7</option>
                                <option value="8">8</option>
                                <option value="9">9</option>
                                <option value="10">10</option>
                            </select>
                        </div>
                    </div>
                    <div style={{ marginTop: "8px", fontFamily: "Outfit", fontSize: "14px", color: "#555" }}>
                        Sample Code: <strong style={{ color: "#362FD8", letterSpacing: "1px" }}>
                            {sampleCode(data.prefix, parseInt(data.code_length))}
                        </strong>
                    </div>
                </div>
            )}

            {/* ── External Base URL (NFC / Codeless) ── */}
            {(data.code_type === "nfc" || data.code_type === "codeless") && (
                <>
                    <label style={labelStyle}>
                        External Base URL
                        <span style={hintStyle}>
                            {data.code_type === "nfc"
                                ? "URL written to NFC chips — code is appended as query param. Can be updated after approval."
                                : "Base URL for codeless verification — code appended automatically. Can be updated after approval."}
                        </span>
                    </label>
                    <input
                        onChange={(e) => setData((prev) => ({ ...prev, externalBaseUrl: e.target.value }))}
                        value={data.externalBaseUrl}
                        placeholder="https://yourdomain.com/verify"
                        style={inputStyle}
                        type="text"
                    />
                    <div style={{ marginTop: "8px", fontFamily: "Outfit", fontSize: "13px", color: "#828282" }}>
                        <span>Each code will get a unique link like: </span>
                        <strong style={{ color: "#362FD8", wordBreak: "break-all" }}>
                            {(data.externalBaseUrl || "yourdomain.com/verify").replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '') + "/?h=A3F8D2K9M1"}
                        </strong>
                    </div>
                </>
            )}
        </CustomModal>
    );
};

export default OrderSettings;
